import { eventChannel } from 'redux-saga';
import {
  put,
  race,
  take,
  fork,
  call,
  delay,
  cancel,
  select,
  debounce,
  takeEvery,
  takeLatest,
} from 'redux-saga/effects';

import { uploadImageAsset } from '../apis/asset';
import { fetchUserDeviceCard } from '../apis/device';
import { errCode, errMessage } from '../../lib/apiError';
import {
  takeAttachments,
  rememberAssetIds,
  releaseAttachments,
  releaseAllAttachments,
} from '../../sections/support/pendingAttachments';
import {
  agentSocketUrl,
  closeAppliesTo,
  isClaimConflict,
  isReauthRequired,
  supportHistoryHint,
  conversationSocketUrl,
} from '../../lib/chat';
import {
  selectInbox,
  selectThread,
  selectThreadId,
  selectAgentSocket,
  selectConsoleVariant,
  selectThreadMessages,
  selectReauthRequired,
} from '../selectors/chat';
import {
  fetchPool,
  fetchAgents,
  sendMessage,
  fetchMessages,
  setAgentStatus,
  fetchConversation,
  fetchMyAgentStatus,
  assignConversation,
  fetchConversations,
  requeueConversation,
  resolveConversation,
  fetchSupportHistory,
  transferConversation,
  markConversationRead,
  fetchConversationAudit,
  fetchCoachConversations,
} from '../apis/chat';
import {
  setInboxPage,
  consoleClosed,
  consoleOpened,
  getPoolAction,
  setInboxQueue,
  getAuditAction,
  getInboxAction,
  setInboxStatus,
  getPoolSuccess,
  getPoolFailure,
  markReadAction,
  reauthRequired,
  getAgentsAction,
  getAuditFailure,
  getAuditSuccess,
  getInboxFailure,
  getInboxSuccess,
  messageReceived,
  retrySendAction,
  loadOlderAction,
  getAgentsSuccess,
  getAgentsFailure,
  openThreadAction,
  closeThreadAction,
  inboxPushReceived,
  sendMessageAction,
  conversationRated,
  conversationLoaded,
  getMessagesFailure,
  getMessagesSuccess,
  sendMessageFailure,
  sendMessageSuccess,
  getDeviceCardAction,
  getDeviceCardSuccess,
  getDeviceCardFailure,
  getAgentStatusAction,
  setAgentStatusAction,
  customerTypingChanged,
  getAgentStatusSuccess,
  setAgentStatusFailure,
  setAgentStatusSuccess,
  getSupportHistoryAction,
  claimConversationAction,
  getSupportHistorySuccess,
  getSupportHistoryFailure,
  agentSocketStatusChanged,
  claimConversationFailure,
  claimConversationSuccess,
  claimConversationConflict,
  conversationActionFailure,
  conversationActionSuccess,
  requeueConversationAction,
  resolveConversationAction,
  threadSocketStatusChanged,
  transferConversationAction,
} from '../slices/chat';

// Miss the app-level ping for 180s and the agent is reaped to offline with no visible
// disconnect - new conversations just stop arriving.
const PING_INTERVAL = 60000;
const INITIAL_BACKOFF = 1000;
const MAX_BACKOFF = 15000;
// How long to wait for the thread socket before backfilling anyway (P1 runs with no socket).
const SOCKET_OPEN_GRACE = 3000;
// Conversation lists have no audit side effect, so they may be re-read while push is down.
const LIST_REFRESH_INTERVAL = 20000;

const parseFrame = (raw) => {
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
};

// The contract doesn't pin the frame envelope; accept the usual Channels spellings.
const frameEvent = (frame) => frame?.event || frame?.type || null;
const framePayload = (frame) => frame?.data ?? frame?.payload ?? frame;

// Emits socket lifecycle only - the caller keeps the WebSocket so it can ping on it.
const socketChannel = (ws) =>
  eventChannel((emit) => {
    ws.onopen = () => emit({ kind: 'open' });
    ws.onmessage = (e) => emit({ kind: 'frame', frame: parseFrame(e.data) });
    ws.onerror = () => emit({ kind: 'error' });
    ws.onclose = () => emit({ kind: 'close' });
    return () => {
      ws.onopen = null;
      ws.onmessage = null;
      ws.onerror = null;
      ws.onclose = null;
      ws.close();
    };
  });

function* reportFailure(error, actionCreator, fallback, extra) {
  if (isReauthRequired(error)) yield put(reauthRequired());
  const message = errMessage(error, fallback);
  yield put(actionCreator(extra ? { ...extra, message } : message));
}

function* pingLoop(ws) {
  for (;;) {
    yield delay(PING_INTERVAL);
    if (ws.readyState === WebSocket.OPEN) ws.send(JSON.stringify({ event: 'ping' }));
  }
}

function* handleAgentFrame(frame) {
  const event = frameEvent(frame);
  if (!event || event === 'ping' || event === 'pong') return;
  const payload = framePayload(frame);
  if (event.startsWith('presence')) {
    if (payload?.status) yield put(setAgentStatusSuccess(payload.status));
    return;
  }
  // Inbox push payloads are unpinned, so re-read the lists (debounced) instead of patching.
  yield put(inboxPushReceived());
}

function* refreshLists() {
  yield put(getPoolAction());
  yield put(getInboxAction());
}

// Reauth parks the reconnect/poll machinery; consoleOpened clears the flag on remount.
function* waitOutReauth() {
  if (yield select(selectReauthRequired)) yield take(consoleOpened.type);
}

function* agentSocketTask() {
  let backoff = INITIAL_BACKOFF;
  for (;;) {
    yield* waitOutReauth();
    const ws = new WebSocket(agentSocketUrl());
    const channel = yield call(socketChannel, ws);
    let ping = null;
    try {
      yield put(agentSocketStatusChanged('connecting'));
      let closed = false;
      while (!closed) {
        const { kind, frame } = yield take(channel);
        if (kind === 'open') {
          backoff = INITIAL_BACKOFF;
          yield put(agentSocketStatusChanged('open'));
          ping = yield fork(pingLoop, ws);
          yield put(getPoolAction());
          yield put(getInboxAction());
        } else if (kind === 'frame') {
          yield* handleAgentFrame(frame);
        } else {
          closed = true;
        }
      }
    } finally {
      if (ping) yield cancel(ping);
      channel.close();
      yield put(agentSocketStatusChanged('closed'));
    }
    yield delay(backoff);
    backoff = Math.min(backoff * 2, MAX_BACKOFF);
  }
}

// List rows omit customer detail, so the header would otherwise show a raw id.
function* loadConversation(id) {
  try {
    const conversation = yield call(fetchConversation, id);
    if (conversation) yield put(conversationLoaded({ ...conversation, id }));
  } catch {
    // Non-fatal: the transcript still works with the seeded list row.
  }
}

function* loadTranscript(id, page = 1) {
  try {
    const response = yield call(fetchMessages, id, { page });
    yield put(
      getMessagesSuccess({
        id,
        messages: response?.data || [],
        page: response?.page || page,
        totalPages: response?.total_pages,
      })
    );
    if (page === 1) yield put(markReadAction(id));
  } catch (error) {
    yield* reportFailure(error, getMessagesFailure, 'Failed to load the transcript', { id });
  }
}

// Older pages are user-requested, so the extra transcript_viewed audit row is expected.
function* handleLoadOlder() {
  const { id, page } = yield select(selectThread);
  if (id) yield call(loadTranscript, id, page + 1);
}

function* clearTypingAfterPause(id) {
  yield delay(5000);
  yield put(customerTypingChanged({ id, isTyping: false }));
}

function* threadSocketTask(id) {
  let backoff = INITIAL_BACKOFF;
  for (;;) {
    yield* waitOutReauth();
    const ws = new WebSocket(conversationSocketUrl(id));
    const channel = yield call(socketChannel, ws);
    let typingTask;
    try {
      yield put(threadSocketStatusChanged('connecting'));
      let closed = false;
      while (!closed) {
        const { kind, frame } = yield take(channel);
        if (kind === 'open') {
          backoff = INITIAL_BACKOFF;
          yield put(threadSocketStatusChanged('open'));
          // Socket first, then REST backfill - the server keeps no replay buffer, so
          // fetching history first drops anything that lands during the fetch.
          yield call(loadTranscript, id);
        } else if (kind === 'frame') {
          const event = frameEvent(frame);
          const payload = framePayload(frame);
          if (event === 'typing' && payload?.sender_type === 'customer') {
            if (typingTask) yield cancel(typingTask);
            yield put(customerTypingChanged({ id, isTyping: payload.is_typing }));
            if (payload.is_typing) typingTask = yield fork(clearTypingAfterPause, id);
          } else if (event === 'message.new' || event === 'message.ack') {
            const message = payload?.message || payload;
            if (message?.sender_type === 'customer') {
              if (typingTask) yield cancel(typingTask);
              yield put(customerTypingChanged({ id, isTyping: false }));
            }
            if (message?.client_msg_id || message?.id) {
              yield put(messageReceived({ id, message }));
            }
          } else if (payload?.conversation) {
            yield put(conversationLoaded(payload.conversation));
            // A resolve frame carries the rating nested; a bare rating frame does not.
            if (payload.conversation.rating === undefined && payload.rating !== undefined) {
              yield put(conversationRated({ id, rating: payload.rating }));
            }
          } else if (payload?.rating !== undefined) {
            yield put(conversationRated({ id, rating: payload.rating }));
          } else if (event && /rat(ing|ed)|feedback|csat/i.test(event)) {
            yield put(conversationRated({ id, rating: payload }));
          }
        } else {
          closed = true;
        }
      }
    } finally {
      channel.close();
      if (typingTask) yield cancel(typingTask);
      yield put(customerTypingChanged({ id, isTyping: false }));
      yield put(threadSocketStatusChanged('closed'));
    }
    yield delay(backoff);
    backoff = Math.min(backoff * 2, MAX_BACKOFF);
  }
}

function* handleOpenThread(action) {
  const id = action.payload;
  if (!id) return;
  yield fork(loadConversation, id);
  const socketTask = yield fork(threadSocketTask, id);
  // The socket backfills on open; this only covers it failing or never getting there.
  const { opened } = yield race({
    opened: take((a) => a.type === threadSocketStatusChanged.type && a.payload === 'open'),
    failed: take((a) => a.type === threadSocketStatusChanged.type && a.payload === 'closed'),
    timedOut: delay(SOCKET_OPEN_GRACE),
  });
  if (!opened) yield call(loadTranscript, id);
  // takeLatest cancels this on another open; leaving must tear it down explicitly. A close
  // naming another thread is a stale transfer and must not cancel this socket.
  yield take(
    (a) =>
      a.type === consoleClosed.type ||
      (a.type === closeThreadAction.type && closeAppliesTo(a.payload, id))
  );
  yield cancel(socketTask);
}

// Uploads run after the bubble is already on screen, so a slow image never
// delays the transcript. Ids are cached, so a retry re-posts the same assets.
function* uploadAttachments(clientMsgId) {
  const parked = takeAttachments(clientMsgId);
  if (!parked) return [];
  if (parked.assetIds) return parked.assetIds;
  const uploaded = yield call(
    [Promise, 'all'],
    parked.files.map((file) => uploadImageAsset(file, file.name))
  );
  const assetIds = uploaded.map((result) => result.assetId).filter(Boolean);
  rememberAssetIds(clientMsgId, assetIds);
  return assetIds;
}

function* postMessage(id, message) {
  try {
    const assetIds = yield* uploadAttachments(message.client_msg_id);
    const sent = yield call(sendMessage, id, {
      client_msg_id: message.client_msg_id,
      body: message.body,
      content_type: message.content_type,
      ...(assetIds.length ? { asset_ids: assetIds } : {}),
      ...(message.macro_id ? { macro_id: message.macro_id } : {}),
    });
    releaseAttachments(message.client_msg_id);
    yield put(sendMessageSuccess({ id, message: sent }));
  } catch (error) {
    if (isReauthRequired(error)) yield put(reauthRequired());
    // The macro was retired or removed and nothing was sent. Detaching it lets Retry send
    // the same text on its own, under the same client_msg_id.
    const macroGone = errCode(error) === 'macro_not_usable';
    yield put(
      sendMessageFailure({
        id,
        clientMsgId: message.client_msg_id,
        dropMacro: macroGone,
        error: macroGone
          ? 'That macro was just retired, so the reply was not sent. Retry sends the text without it.'
          : errMessage(error, 'Message not sent'),
      })
    );
  }
}

function* handleSendMessage(action) {
  const { id, message } = action.payload;
  yield call(postMessage, id, message);
}

function* handleRetrySend(action) {
  const { id, clientMsgId } = action.payload;
  const messages = yield select(selectThreadMessages);
  const original = messages.find((m) => m.client_msg_id === clientMsgId);
  // Same client_msg_id: the server dedupes and hands back the original message.
  if (original) yield call(postMessage, id, original);
}

function* handleGetAgentStatus() {
  try {
    const response = yield call(fetchMyAgentStatus);
    yield put(getAgentStatusSuccess(response));
  } catch (error) {
    yield* reportFailure(error, setAgentStatusFailure, 'Could not load your availability');
  }
}

function* handleSetAgentStatus(action) {
  try {
    const response = yield call(setAgentStatus, action.payload);
    yield put(setAgentStatusSuccess(response?.status || action.payload));
  } catch (error) {
    yield* reportFailure(error, setAgentStatusFailure, 'Failed to change your status');
  }
}

function* handleGetPool() {
  const { queue } = yield select(selectInbox);
  // Coaching is routed, not claimed, so the coach console has no pool to read.
  if ((yield select(selectConsoleVariant)) === 'coach') {
    yield put(getPoolSuccess({ queue, items: [], total: 0 }));
    return;
  }
  try {
    const response = yield call(fetchPool, { queue });
    yield put(
      getPoolSuccess({ queue, items: response?.data || [], total: response?.total_records || 0 })
    );
  } catch (error) {
    yield* reportFailure(error, getPoolFailure, 'Failed to load waiting chats');
  }
}

function* handleGetInbox() {
  try {
    const { status, queue, page } = yield select(selectInbox);
    const variant = yield select(selectConsoleVariant);
    // Tabs are server-side now, and the two consoles have different vocabularies.
    // Coaching is the coach console's only queue; the support inbox has to name one.
    const fetcher = variant === 'coach' ? fetchCoachConversations : fetchConversations;
    const response = yield call(
      fetcher,
      variant === 'coach' ? { status, page } : { status, queue, page }
    );
    // Carry the tab it was fetched for, so a late response can be matched to it.
    yield put(
      getInboxSuccess({
        status,
        queue,
        page,
        total: response?.total_records || 0,
        items: response?.data || [],
      })
    );
  } catch (error) {
    yield* reportFailure(error, getInboxFailure, 'Failed to load your inbox');
  }
}

function* handleGetAgents(action) {
  try {
    const response = yield call(fetchAgents, action?.payload || {});
    // The roster envelope isn't pinned by the contract - accept the paginated
    // wrapper, a {results} wrapper, or a bare array.
    const items = Array.isArray(response) ? response : response?.data || response?.results || [];
    yield put(getAgentsSuccess(items));
  } catch (error) {
    // Surfaced in the transfer dialog: an empty picker with no reason is worse than an error.
    yield* reportFailure(error, getAgentsFailure, 'Could not load the agent roster');
  }
}

function* handleClaim(action) {
  const id = action.payload;
  try {
    const conversation = yield call(assignConversation, id);
    yield put(claimConversationSuccess(conversation));
    yield put(openThreadAction(id));
  } catch (error) {
    if (isClaimConflict(error)) {
      yield put(claimConversationConflict(id));
      return;
    }
    yield* reportFailure(error, claimConversationFailure, 'Failed to claim the conversation');
  }
}

function* handleConversationAction(apiCall, args, fallback) {
  // Resolve, transfer and requeue each watch their own action, so opening another
  // thread does not cancel them - both outcomes must name the thread they belong to.
  const [id] = args;
  try {
    const conversation = yield call(apiCall, ...args);
    yield put(conversationActionSuccess({ id, conversation }));
    yield put(getInboxAction());
    return true;
  } catch (error) {
    yield* reportFailure(error, conversationActionFailure, fallback, { id });
    return false;
  }
}

function* handleResolve(action) {
  const { id, reason, status } = action.payload;
  yield* handleConversationAction(
    resolveConversation,
    [id, { reason, status }],
    'Failed to resolve the conversation'
  );
}

// Transfer and requeue both hand the thread away, so the console stops following it.
function* handleTransfer(action) {
  const { id, agentId, reason } = action.payload;
  const ok = yield* handleConversationAction(
    transferConversation,
    [id, { agentId, reason }],
    'Failed to transfer the conversation'
  );
  if (ok) yield put(closeThreadAction(id));
}

function* handleRequeue(action) {
  const { id, queue, reason } = action.payload;
  const ok = yield* handleConversationAction(
    requeueConversation,
    [id, { queue, reason }],
    'Failed to requeue the conversation'
  );
  if (ok) {
    yield put(closeThreadAction(id));
    yield put(getPoolAction());
  }
}

function* handleMarkRead(action) {
  try {
    yield call(markConversationRead, action.payload);
  } catch {
    // Read receipts are advisory; never block the transcript on one.
  }
}

function* handleGetSupportHistory(action) {
  const { conversationId, customerId, queue } = action.payload;
  try {
    // Keyed by the customer, but guarded by the thread: the panel shows one thread, so
    // a late reply for the thread the agent already left must not land.
    const response = yield call(fetchSupportHistory, customerId, { queue });
    yield put(getSupportHistorySuccess({ conversationId, items: response?.data || [] }));
  } catch (error) {
    // Route on error.code first; errMessage would surface the server's prose instead.
    const hint = supportHistoryHint(error);
    if (hint) yield put(getSupportHistoryFailure({ conversationId, message: hint }));
    else {
      yield* reportFailure(
        error,
        getSupportHistoryFailure,
        "Failed to load this customer's support history",
        { conversationId }
      );
    }
  }
}

function* handleGetDeviceCard(action) {
  const userId = action.payload;
  try {
    const response = yield call(fetchUserDeviceCard, userId);
    yield put(getDeviceCardSuccess({ userId, data: response?.data || null }));
  } catch (error) {
    // The device card is admin and support-agent only; a coach gets 403, not an outage.
    const fallback =
      error?.response?.status === 403
        ? 'Device details are limited to admins and support agents.'
        : 'Failed to load the device card';
    yield* reportFailure(error, getDeviceCardFailure, fallback, { userId });
  }
}

function* handleGetAudit(action) {
  try {
    const response = yield call(fetchConversationAudit, action.payload);
    yield put(getAuditSuccess(response?.results || []));
  } catch (error) {
    yield* reportFailure(error, getAuditFailure, 'Failed to load the audit trail');
  }
}

// Only while push is down, and only for lists - the transcript writes an audit row per read.
function* listRefreshTask() {
  for (;;) {
    yield delay(LIST_REFRESH_INTERVAL);
    yield* waitOutReauth();
    const socket = yield select(selectAgentSocket);
    if (socket !== 'open' && document.visibilityState === 'visible') {
      yield put(getPoolAction());
      yield put(getInboxAction());
    }
  }
}

// All console watchers live here so consoleClosed cancels them, in-flight fetches included.
function* consoleWatchers() {
  yield takeLatest([getPoolAction.type, setInboxQueue.type], handleGetPool);
  // One watcher, so takeLatest cancels a refresh that overlaps a tab change.
  yield takeLatest(
    [getInboxAction.type, setInboxStatus.type, setInboxQueue.type, setInboxPage.type],
    handleGetInbox
  );
  yield takeLatest(getAgentsAction.type, handleGetAgents);
  yield takeEvery(claimConversationAction.type, handleClaim);
  yield takeLatest(openThreadAction.type, handleOpenThread);
  yield takeEvery(sendMessageAction.type, handleSendMessage);
  yield takeEvery(retrySendAction.type, handleRetrySend);
  yield takeLatest(resolveConversationAction.type, handleResolve);
  yield takeLatest(transferConversationAction.type, handleTransfer);
  yield takeLatest(requeueConversationAction.type, handleRequeue);
  yield takeEvery(markReadAction.type, handleMarkRead);
  yield takeLatest(getAuditAction.type, handleGetAudit);
  yield takeLatest(getSupportHistoryAction.type, handleGetSupportHistory);
  yield takeLatest(getDeviceCardAction.type, handleGetDeviceCard);
  yield takeLatest(loadOlderAction.type, handleLoadOlder);
  yield debounce(1000, inboxPushReceived.type, refreshLists);
}

function* handleConsoleOpened() {
  const watchers = yield fork(consoleWatchers);
  const socketTask = yield fork(agentSocketTask);
  const refreshTask = yield fork(listRefreshTask);
  yield put(getPoolAction());
  yield put(getInboxAction());
  yield put(getAgentsAction());
  yield take(consoleClosed.type);
  yield cancel(watchers);
  yield cancel(socketTask);
  yield cancel(refreshTask);
}

// Files kept for a retry that can no longer happen, and the blob URLs behind every
// preview, are only safe to drop once the thread is gone.
function* discardParkedAttachments(action) {
  // Releasing is global, so a stale close for a thread the agent already left would
  // throw away the attachments parked against the one open now.
  if (action.type === closeThreadAction.type) {
    const openId = yield select(selectThreadId);
    if (!closeAppliesTo(action.payload, openId)) return;
  }
  yield call(releaseAllAttachments);
}

export default function* chatSaga() {
  // The panel headers set presence too, not just the console, so this is always live.
  yield takeLatest(getAgentStatusAction.type, handleGetAgentStatus);
  yield takeLatest(setAgentStatusAction.type, handleSetAgentStatus);
  yield takeLatest(consoleOpened.type, handleConsoleOpened);
  yield takeEvery([closeThreadAction.type, consoleClosed.type], discardParkedAttachments);
}
